import { useTranslation } from "react-i18next";

import { useFieldArray, useFormContext } from "react-hook-form";

import makeStyles from "@mui/styles/makeStyles";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";

import { InputText } from "primereact/inputtext";

import { AddButton } from "../../../shared/FormLayout/IconButtons/AddButton";
import { DeleteButton } from "../../../shared/FormLayout/IconButtons/DeleteButton";
import { ValidationMessage } from "../../../shared/ValidationMessage";
import { MessageCard, MessageType } from "../../../shared/MessageCard";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    width: "100%",
  },
  textButton: {
    textTransform: "uppercase",
  },
  list: {
    width: "100%",
  },
  inputText: {
    width: "100%",
  },
  validation: {
    paddingLeft: theme.spacing(2),
  },
}));

interface DropdownBuilderProps {
  includeOther: boolean;
  onIncludeOtherCheckboxChange: (checked: boolean) => void;
}

export function DropdownBuilder(props: DropdownBuilderProps) {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    control,
    register,
    unregister,
    formState: { errors },
    clearErrors,
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "formOptions",
  });

  function handleAddDropdownOption() {
    clearErrors("formOptions");
    append("");
  }

  function handleRemoveDropdownOption(index: number) {
    unregister(`formOptions.${index}.value`);
    remove(index);
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    props.onIncludeOtherCheckboxChange(checked);
  };

  return (
    <div className={classes.root}>
      {fields && fields.length > 0 ? (
        <>
          <List className={classes.list}>
            {fields.map((field, index) => (
              <div key={`listItemContainer-${field.id}`}>
                <ListItem key={`listItem-${field.id}`} dense>
                  <InputText
                    className={classes.inputText}
                    key={field.id} // important to use useFieldArray field's id as key
                    {...register(`formOptions.${index}.value` as const, {
                      required: true,
                    })}
                  />

                  <DeleteButton key={`deleteButton-${field.id}`} onClick={() => handleRemoveDropdownOption(index)} />
                </ListItem>
                <div key={`validationContainer-${field.id}`}>
                  {errors.formOptions && errors.formOptions[index]?.value?.type === "required" && (
                    <ValidationMessage
                      key={`validationMessage-${field.id}`}
                      className={classes.validation}
                      invalidMessage={t("customQuestions.optionTextEmpty")}
                      validationType="error"
                      showIcon
                    />
                  )}
                </div>
              </div>
            ))}
          </List>

          <AddButton onClick={handleAddDropdownOption} />
        </>
      ) : (
        <MessageCard
          messageType={MessageType.Info}
          title={t("customQuestions.dropdownEmpty")}
          secondaryText={t("customQuestions.noDropdownOptions")}
          cardActions={
            <Button size="small" color="primary" className={classes.textButton} onClick={handleAddDropdownOption}>
              {t("customQuestions.addOption")}
            </Button>
          }
        />
      )}

      {errors.formOptions && errors.formOptions.type === "listHasOptions" && (
        <ValidationMessage invalidMessage={t("customQuestions.enterOptions")} validationType="error" showIcon />
      )}

      <ListItem dense>
        <ListItemIcon>
          <Checkbox
            checked={props.includeOther}
            tabIndex={-1}
            disableRipple
            readOnly
            color="primary"
            onChange={handleCheckboxChange}
          />
        </ListItemIcon>
        <ListItemText primary={t("customQuestions.includeOther")} />
      </ListItem>
    </div>
  );
}
